/**
 * React Query hooks for an app's picture sharing (Community Assets).
 *
 * Endpoints:
 *   GET /contributor/arches/:archeId/community-assets/settings
 *   PUT /contributor/arches/:archeId/community-assets/settings
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export function useCommunitySharing(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.communitySharing(archeId),
    queryFn: () => api.getCommunitySharing(archeId),
    enabled: !!archeId,
    // A 403 is an answer ("not your bill"), not a failure worth retrying.
    retry: (count, err) => !(err instanceof ApiError && err.status === 403) && count < 2,
  });
}

export function useSetCommunitySharing(archeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) => api.setCommunitySharing(archeId, enabled),
    // The PUT answers with the switch only; refetch so the usage beside it
    // is read fresh too.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: queryKeys.contributorArches.communitySharing(archeId),
      }),
  });
}
